/* global window URLSearchParams */

import * as React from "react";
import { useSlideContext } from "./components/SlideContext";
import Button from "./components/Button";
import PPTWindow from "./components/PPTWindow";
import InfoBox from "./components/InfoBox";
import VerticalCenter from "./components/VerticalCenter";
import StyledTable from "./components/StyledTable";
import slotIcon from "./assets/handle slot.svg";
import columnIcon from "./assets/handle column.svg";
import pptmashup from "./assets/pptmashup.png";
import pptmashup800 from "./assets/pptmashup-800.png";
import pptmashup400 from "./assets/pptmashup-400.png";
import mouseIcon from "./assets/pointer.svg";
import promptIcon from "./assets/prompt.svg";
import floatingTemplates from "./assets/floating_templates.png";
import floatingTemplates1600 from "./assets/floating_templates-1600.png";
import floatingTemplates800 from "./assets/floating_templates-800.png";
import floatingTemplates400 from "./assets/floating_templates-400.png";
import useAIIcon from "./assets/use_ai.png";
import noAIIcon from "./assets/no_ai.png";
import Arrow from "./components/Arrow";
import { data1, data2 } from "./data";
import useParsedTableData from "./hooks/useParsedTableData";
import { useEffect } from "react";
import { isMacOS } from "@tiptap/react";
import useWindowWidth from "./hooks/useWindowWidth";
import XLSWindow from "./components/XLSWindow";
import ChatGPTWindow from "./components/ChatGPTWindow";
import CopyContentButton from "./components/CopyContentButton";

const Slides = [
  {
    id: "intro",
    step: 1,
    noNextDelay: true,
    next: "welcome",
    title: "See how to be 15x more productive with Gridd",
    subtitle: "Click through this brief interactive guide",
    content: (
      <>
        <img
          style={{ width: "100%", maxWidth: "1000px" }}
          src={pptmashup}
          srcSet={`${pptmashup400} 400w, ${pptmashup800} 800w, ${pptmashup} 1600w`}
          sizes="(max-width: 1000px) 100vw, 1000px"
          alt={"Sample presentations showing Gridd functionality in PowerPoint"}
        />
        <p style={{ maxWidth: "600px" }}>
          Gridd is added to a page as an object, like shapes or text, but inside the object there are menus you use to
          format content. PowerPoint buttons won&#39;t work.
        </p>
      </>
    ),
  },
  {
    id: "welcome",
    step: 2,
    title: "Which describes you best?",
    subtitle: "We’ll tailor your onboarding experience to meet your needs",
    Component: () => {
      const { setProp, goTo } = useSlideContext();

      useEffect(() => {
        setProp("totalSteps", 7);
      }, []);

      return (
        <VerticalCenter>
          <div
            style={{
              flex: 1,
              display: "flex",
              gap: "10px",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <img
              src={floatingTemplates}
              srcSet={`${floatingTemplates400} 400w, ${floatingTemplates800} 800w, ${floatingTemplates1600} 1600w, ${floatingTemplates} 2340w`}
              sizes="(max-width: 1000px) 40vw, 400px"
              style={{ width: "40%", height: "auto", alignSelf: "center", borderRadius: "10px", maxWidth: "1000px" }}
              alt="Floating templates"
            />
            <div
              style={{
                maxWidth: "500px",
                flex: 1,
                display: "flex",
                flexDirection: "column",
                gap: "10px",
              }}
            >
              <Button
                style={{
                  flex: 1,
                  height: "auto",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-start",
                  paddingTop: "17px",
                  paddingBottom: "17px",
                  gap: "10px",
                }}
                onClick={() => {
                  setProp("totalSteps", 7);
                  setProp("useAI", "yes");
                  goTo("add_column");
                }}
              >
                <img src={useAIIcon} style={{ height: "50px", marginLeft: "5px", marginRight: "3px" }} alt="Use AI" />I
                mostly plan to use AI for slide content creation
              </Button>
              <Button
                style={{
                  flex: 1,
                  height: "auto",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-start",
                  paddingTop: "17px",
                  paddingBottom: "17px",
                  gap: "10px",
                }}
                onClick={() => {
                  setProp("totalSteps", 7);
                  setProp("useAI", "no");
                  goTo("add_column");
                }}
              >
                <img src={noAIIcon} style={{ height: "50px" }} alt="No AI" />
                I&#39;m not planning on using AI for slide content creation
              </Button>
              <Button
                style={{
                  flex: 1,
                  height: "auto",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-start",
                  gap: "10px",
                  paddingTop: "12px",
                  paddingBottom: "12px",
                }}
                onClick={() => {
                  setProp("totalSteps", 7);
                  setProp("useAI", "maybe");
                  goTo("add_column");
                }}
              >
                Not sure
              </Button>
            </div>
          </div>
        </VerticalCenter>
      );
    },
  },
  {
    id: "add_column",
    step: 3,
    next: "rearrange_steps",
    title: "See how you can expand the templates",
    subtitle: "Add a column without needing any re-formatting",
    content: (
      <>
        <InfoBox
          label={
            <>
              <strong>Try it now!</strong> Add a column by clicking{" "}
              <img alt="Plus icon" style={{ width: "16px", marginBottom: "-3px" }} src={slotIcon} /> below
            </>
          }
          style={{
            top: "10px",
            left: "50%",
            marginLeft: "-210px",
          }}
        />
        <Arrow start={{ x: "50%", y: "70px" }} end={{ x: "50%", y: "71px" }} />
        <PPTWindow slideId={3} />
      </>
    ),
  },
  {
    id: "rearrange_steps",
    step: 4,
    next: ({ useAI }) => {
      switch (useAI) {
        case "no":
          return "auto_expand_no_ai";
        default:
          return "auto_expand";
      }
    },
    title: "Drag or swap without re-aligning",
    subtitle: "Gridd templates can be rearranged without braking formatting",
    Component: () => {
      return (
        <>
          <InfoBox
            label={
              <>
                Reorder columns by hovering over, clicking and dragging{" "}
                <img alt="Column handle icon" style={{ height: "20px", marginBottom: "-3px" }} src={columnIcon} />
              </>
            }
            style={{
              top: "8px",
              left: "50%",
              marginLeft: "-210px",
            }}
          />
          <Arrow start={{ x: "calc(50% + 87px)", y: "70px" }} end={{ x: "calc(50% + 87px)", y: "71px" }} />
          <PPTWindow slideId={4} />
        </>
      );
    },
  },
  {
    id: "auto_expand",
    step: 5,
    title: "See how the templates auto-expand",
    subtitle: "Copy this table from ChatGPT into the Gridd template",
    Component: () => {
      const { goTo } = useSlideContext();
      const { headers, rows } = useParsedTableData(data2);

      return (
        <VerticalCenter>
          <ChatGPTWindow>
            <StyledTable dense headers={headers} rows={rows} />
          </ChatGPTWindow>
          <div style={{ marginTop: "30px" }}>
            <CopyContentButton
              primary
              style={{ width: "300px" }}
              content={data2}
              onDone={() => goTo("auto_expand_paste")}
            >
              Click to copy table data
            </CopyContentButton>
          </div>
        </VerticalCenter>
      );
    },
  },
  {
    id: "auto_expand_paste",
    next: ({ useAI }) => (useAI === "maybe" ? "right_click_menu" : "framework"),
    step: 5,
    title: "See how the templates auto-expand",
    subtitle: "Copy this table from ChatGPT into the Gridd template",
    Component: () => {
      const {
        pptFrame: { width },
      } = useSlideContext();
      return (
        <>
          <InfoBox
            label={`Click in the first cell and paste (${isMacOS() ? "Cmd" : "Ctrl"}+V)`}
            style={{
              top: "10px",
              left: `calc(50% - ${width * 0.55}px)`,
            }}
          />
          <Arrow
            start={{ x: `calc(50% - ${width * 0.35}px)`, y: "70px" }}
            end={{ x: `calc(50% - ${width * 0.35}px)`, y: "71px" }}
          />
          <PPTWindow slideId={11} />
        </>
      );
    },
  },
  {
    id: "auto_expand_no_ai",
    step: 5,
    title: "See how the templates auto-expand",
    subtitle: "Copy this table into the Gridd template",
    Component: () => {
      const { goTo } = useSlideContext();
      const { headers, rows } = useParsedTableData(data1);

      return (
        <VerticalCenter>
          <XLSWindow>
            <StyledTable headers={headers} rows={rows} />
          </XLSWindow>
          <div style={{ marginTop: "30px" }}>
            <CopyContentButton
              style={{ width: "300px" }}
              primary
              content={data1}
              onDone={() => goTo("auto_expand_no_ai_paste")}
            >
              Click to copy table data
            </CopyContentButton>
          </div>
        </VerticalCenter>
      );
    },
  },
  {
    id: "auto_expand_no_ai_paste",
    next: "right_click_menu",
    step: 5,
    title: "See how the templates auto-expand",
    subtitle: "Copy this table into the Gridd template",
    Component: () => {
      const {
        pptFrame: { width },
      } = useSlideContext();
      return (
        <>
          <InfoBox
            label={`Click in the first cell and paste (${isMacOS() ? "Cmd" : "Ctrl"}+V)`}
            style={{
              top: "10px",
              left: `calc(50% - ${width * 0.55}px)`,
            }}
          />
          <Arrow
            start={{ x: `calc(50% - ${width * 0.45}px)`, y: "70px" }}
            end={{ x: `calc(50% - ${width * 0.45}px)`, y: "71px" }}
          />
          <PPTWindow slideId={7} />
        </>
      );
    },
  },
  {
    id: "right_click_menu",
    next: new URLSearchParams(window.location.search).get("source") === "one_pager" ? "done_redirect" : "done",
    step: 6,
    title: "Quick edit without breaking formatting",
    subtitle: "Right click to modify or add shapes, icons, or images",
    content: (
      <div style={{ width: "90%", display: "flex", flexDirection: "row", gap: 0 }}>
        <div style={{ flex: 20, position: "relative" }}>
          <InfoBox
            icon={<img alt="Mouse icon" style={{ height: "50px" }} src={mouseIcon} />}
            label="Right click to modify or add shapes, icons, or images"
            style={{
              top: "25%",
            }}
          />
        </div>
        <div style={{ flex: 10, position: "relative" }}>
          <Arrow start={{ x: "0", y: "50%" }} end={{ x: "100%", y: "50%" }} />
        </div>
        <div style={{ flex: 70, marginLeft: "10px" }}>
          <PPTWindow slideId={8} />
        </div>
      </div>
    ),
  },
  {
    id: "framework",
    next: new URLSearchParams(window.location.search).get("source") === "one_pager" ? "done_redirect" : "done",
    step: 6,
    title: "Define the framework, let AI fill the content",
    subtitle: "Use the Gridd prompt in your AI Chatbot of choice",
    Component: () => {
      const {
        pptFrame: { y, height },
      } = useSlideContext();
      const windowWidth = useWindowWidth();
      const arrowV = y + height * 0.865;
      return (
        <div style={{ width: "90%", display: "flex", flexDirection: "row", gap: 0 }}>
          <div style={{ flex: 70, marginRight: "10px" }}>
            <PPTWindow slideId={12} />
          </div>
          <div style={{ flex: 10, position: "relative" }}>
            <Arrow start={{ x: "100%", y: `${arrowV}px` }} end={{ x: "0", y: `${arrowV}px` }} />
          </div>
          <div style={{ flex: 20, position: "relative" }}>
            <InfoBox
              icon={<img alt="AI prompt icon" style={{ height: "50px" }} src={promptIcon} />}
              label={
                <ol style={{ marginBlockStart: 0, paddingInlineStart: "25px" }}>
                  <li style={{ marginBottom: "4px" }}>Copy this prompt into ChatGPT, Copilot, or Claude.</li>
                  <li style={{ marginBottom: "4px" }}>
                    Replace the{" "}
                    <code style={{ background: "#eee", padding: "1px" }}>{"{USER, ADD THE SOURCE HERE}"}</code>{" "}
                    placeholder with a reference to what the AI should use
                  </li>
                  <li>Paste the result back into Gridd to get a formatted slide</li>
                </ol>
              }
              style={{
                top: windowWidth > 1200 ? "25%" : "10%",
              }}
            />
          </div>
        </div>
      );
    },
  },
  {
    id: "done_redirect",
    step: 7,
    title: "That's it!",
    href: "https://www.griddtemplates.com/interactive-tour-end",
  },
  {
    id: "done",
    step: 7,
    title: "That's it!",
    subtitle:
      new URLSearchParams(window.location.search).get("source") === "addin"
        ? "Switch back to PowerPoint and start using Gridd!"
        : "Check your email for the free sample.",
    Component: () => {
      const isFromAddin = new URLSearchParams(window.location.search).get("source") === "addin";
      if (!isFromAddin) return <></>;
      return (
        <VerticalCenter>
          <Button primary onClick={() => window.close()} style={{ maxWidth: "220px", maxHeight: "50px" }}>
            Close Tutorial
          </Button>
        </VerticalCenter>
      );
    },
  },
];

export default Slides;
